import { lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PreviewPage from './components/PreviewPage';
import LoginPage from './components/LoginPage';
import DashboardLayout from './components/Dashboard/DashboardLayout';
import DashboardHome from './components/Dashboard/DashboardHome';
import BillingPage from './components/Dashboard/BillingPage';
import WebsiteStatsRedirect from './components/Dashboard/WebsiteStatsRedirect';
import SettingsPage from './components/Dashboard/SettingsPage';
import WebsitesPage from './components/Dashboard/WebsitesPage';
import LeadsPage from './components/Dashboard/LeadsPage';
import BookingsPage from './components/Dashboard/BookingsPage';
import ReviewsPage from './components/Dashboard/ReviewsPage';
import ActivityPage from './components/Dashboard/ActivityPage';
import AnalyticsPage from './components/Dashboard/AnalyticsPage';
import SEOPage from './components/Dashboard/SEOPage';
import WebsiteEditPage from './components/Dashboard/WebsiteEditPage';
import CustomDomainPage from './components/Dashboard/CustomDomainPage';
import AccountLayout from './components/Dashboard/AccountLayout';
import ProfilePage from './components/Dashboard/Account/ProfilePage';
import PasswordPage from './components/Dashboard/Account/PasswordPage';
import NotificationsPage from './components/Dashboard/Account/NotificationsPage';
import AllNotificationsPage from './components/Dashboard/AllNotificationsPage';
import ContentPlanPage from './components/Dashboard/ContentPlanPage';
import PrintKitPage from './components/Dashboard/PrintKitPage';
import PaymentCancel from './components/PaymentCancel';
import VerifyEmailPage from './components/VerifyEmailPage';
import InvitePage from './components/InvitePage';
import ClaimPage from './components/ClaimPage';
import WelcomePage from './components/Welcome/WelcomePage';
import AuthProvider from './components/AuthProvider';
import ProtectedRoute from './components/ProtectedRoute';
import PlanGate from './components/PlanGate';
import ErrorBoundary from './components/ErrorBoundary';
import NotFoundPage from './components/NotFoundPage';
import AdminLayout from './components/Admin/AdminLayout';
import AdminDashboard from './components/Admin/AdminDashboard';
import AdminUsersPage from './components/Admin/AdminUsersPage';
import AdminWebsitesPage from './components/Admin/AdminWebsitesPage';
import AdminLeadsPage from './components/Admin/AdminLeadsPage';
import AdminMessagesPage from './components/Admin/AdminMessagesPage';
import GenerateWebsitePage from './components/Admin/GenerateWebsitePage';
import { ToastProvider } from './components/ui';
import ShowcaseSitePreview from './components/ShowcaseSitePreview';
import DemoDashboard from './demo/DemoDashboard';

// Design labs never ship: the route only registers in dev, and the DEV-gated
// dynamic import keeps the chunk out of the production graph entirely.
const PackLab = lazy(() => import('./pages/PackLab'));

// Configure axios defaults - using Vite proxy, so no baseURL needed

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <ErrorBoundary>
            <Router>
              <div className="App">
                <Routes>
                  {/* Public routes */}
                  <Route path="/preview/:previewId" element={<PreviewPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route
                    path="/verify-email/:token"
                    element={<VerifyEmailPage />}
                  />
                  <Route path="/invite/:token" element={<InvitePage />} />
                  {/* fnd-k1d.4: /api/claim/:slug lands here with the invite in
                  an HttpOnly handoff cookie instead of in the URL. */}
                  <Route path="/claim" element={<ClaimPage />} />
                  <Route
                    path="/showcase-site"
                    element={<ShowcaseSitePreview />}
                  />
                  {import.meta.env.DEV && (
                    <Route
                      path="/lab/pack"
                      element={
                        <Suspense fallback={null}>
                          <PackLab />
                        </Suspense>
                      }
                    />
                  )}
                  {/* Public demo tour (fnd-hi6.4): the real dashboard on
                  fixture data — no login, no backend, nothing saved. Mirrors
                  the /dashboard child routes so the chrome navigates normally;
                  anything else under /demo falls back to the tour's home. */}
                  <Route path="/demo" element={<DemoDashboard />}>
                    <Route index element={<DashboardHome />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route
                      path="seo"
                      element={
                        <PlanGate feature="seo">
                          <SEOPage />
                        </PlanGate>
                      }
                    />
                    <Route path="leads" element={<LeadsPage />} />
                    <Route path="bookings" element={<BookingsPage />} />
                    <Route path="reviews" element={<ReviewsPage />} />
                    <Route path="content-plan" element={<ContentPlanPage />} />
                    <Route path="print-kit" element={<PrintKitPage />} />
                    <Route
                      path="notifications"
                      element={<AllNotificationsPage />}
                    />
                    <Route path="*" element={<Navigate to="/demo" replace />} />
                  </Route>

                  {/* Post-purchase setup tracker. Public route: it renders its own
                  loading/empty states, and `?demo=1` runs it with no backend. */}
                  <Route path="/welcome" element={<WelcomePage />} />
                  {/* Payment routes. fnd-k1d.7 removed /payment/success
                  along with the legacy self-serve onboarding flow it drove;
                  Stripe returns to /welcome or /dashboard/billing. */}
                  <Route path="/payment/cancel" element={<PaymentCancel />} />

                  {/* Protected dashboard routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<DashboardHome />} />
                    <Route path="websites" element={<WebsitesPage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route
                      path="seo"
                      element={
                        <PlanGate feature="seo">
                          <SEOPage />
                        </PlanGate>
                      }
                    />
                    <Route path="leads" element={<LeadsPage />} />
                    <Route path="bookings" element={<BookingsPage />} />
                    <Route path="reviews" element={<ReviewsPage />} />
                    <Route path="content-plan" element={<ContentPlanPage />} />
                    <Route path="print-kit" element={<PrintKitPage />} />
                    <Route
                      path="notifications"
                      element={<AllNotificationsPage />}
                    />
                    {/* Legacy routes - redirect to new account section */}
                    <Route
                      path="billing"
                      element={
                        <Navigate to="/dashboard/account/billing" replace />
                      }
                    />
                    <Route
                      path="settings"
                      element={
                        <Navigate to="/dashboard/account/profile" replace />
                      }
                    />
                    {/* Account section with sidebar */}
                    <Route path="account" element={<AccountLayout />}>
                      <Route
                        index
                        element={
                          <Navigate to="/dashboard/account/billing" replace />
                        }
                      />
                      <Route path="billing" element={<BillingPage />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="domain" element={<CustomDomainPage />} />
                      <Route path="password" element={<PasswordPage />} />
                      <Route
                        path="notifications"
                        element={<NotificationsPage />}
                      />
                      <Route path="activity" element={<ActivityPage />} />
                    </Route>
                    {/* Website management routes */}
                    <Route
                      path="websites/:id/edit"
                      element={<WebsiteEditPage />}
                    />
                    {/* Legacy intake routes — the forms they rendered posted to
                        endpoints that never existed. Send them to the real editor. */}
                    <Route
                      path="websites/:id/setup"
                      element={<WebsiteEditPage />}
                    />
                    <Route
                      path="websites/:id/update"
                      element={<WebsiteEditPage />}
                    />
                    <Route
                      path="websites/:id/stats"
                      element={<WebsiteStatsRedirect />}
                    />
                    <Route
                      path="websites/:id/domain"
                      element={<CustomDomainPage />}
                    />
                  </Route>

                  {/* Admin routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="websites" element={<AdminWebsitesPage />} />
                    <Route path="leads" element={<AdminLeadsPage />} />
                    <Route path="messages" element={<AdminMessagesPage />} />
                    <Route path="generate" element={<GenerateWebsitePage />} />
                  </Route>

                  {/* Default redirect */}
                  <Route
                    path="/"
                    element={<Navigate to="/dashboard" replace />}
                  />

                  {/* 404 catch-all route */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </div>
            </Router>
          </ErrorBoundary>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
